import { store } from '../Store/store';
import { logoutUser } from '../Store/actions/userActions';
import { API_BASE_URL } from '../config/runtimeConfig';
import englishMessages from '../generated/messages.en';

class ApiService {
  // Enhanced fetch with automatic 401 handling
  static async fetch(url, options = {}) {
    const token = localStorage.getItem('token');

    // Prepend API base URL if URL is relative
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

    // Add authorization header if token exists
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const fetchOptions = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(fullUrl, fetchOptions);

      // Handle 401 Unauthorized errors
      if (response.status === 401) {
        console.warn('401 Unauthorized detected - logging out user');

        // Show user notification
        this.showLogoutNotification();

        // Dispatch logout action to clear state and redirect
        store.dispatch(logoutUser());

        // Small delay to let user see the notification before redirect
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);

        // Return rejected promise to prevent further processing
        throw new Error('Session expired. Please log in again.');
      }

      return response;
    } catch (error) {
      // If it's a network error, re-throw it
      if (error.message === 'Session expired. Please log in again.') {
        throw error;
      }

      // For other errors, check if it might be auth related
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Convenience method for chat API calls
  static async chatRequest(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}/chat${endpoint}`;
    return this.fetch(url, options);
  }

  /**
   * Multipart Talk mode turn. Do not set Content-Type — browser sets boundary.
   */
  static async chatVoiceRequest(formData) {
    const token = localStorage.getItem('token');
    const url = `${API_BASE_URL}/chat/voice`;
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401) {
      this.showLogoutNotification();
      store.dispatch(logoutUser());
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
      throw new Error('Session expired. Please log in again.');
    }

    return response;
  }

  // Convenience method for profile API calls
  static async profileRequest(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return this.fetch(url, options);
  }

  // Method to check if response is ok and parse JSON
  static async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Show logout notification to user
  static showLogoutNotification() {
    // Create and show a temporary notification
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #f87171;
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        font-weight: 500;
        max-width: 350px;
        animation: slideIn 0.3s ease-out;
      ">
        ${englishMessages['auth.sessionExpired']}
      </div>
      <style>
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      </style>
    `;

    document.body.appendChild(notification);

    // Remove notification after redirect
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 2500);
  }
}

export default ApiService;
